import type { AxiosError } from 'axios';
import axios from 'axios';

const LOOKER_EMBED_BASE_URL = '/looker-embed';
const HTTP_STATUS_GONE = 410;

export type CookielessClientTokens = {
  authentication_token: string;
  authentication_token_ttl: number;
  navigation_token: string;
  navigation_token_ttl: number;
  api_token: string;
  api_token_ttl: number;
  session_reference_token_ttl: number;
};

export type DashboardItem = {
  id: string;
  title: string;
  category: string | null;
  sortOrder: number;
};

export type LookerAccessLevel = 'view' | 'explore';

export type LookerContentResponse = {
  personalDashboards: DashboardItem[];
  accessLevel: LookerAccessLevel;
  lookerHost: string;
};

export type GenerateTokensRequest = {
  navigation_token: string;
  api_token: string;
};

/**
 * Thrown when the backend returns 410 Gone from the session/generate endpoint.
 * Signals the SDK that the session is dead and must be re-acquired.
 */
export class SessionExpiredError extends Error {
  constructor(message = 'Looker cookieless session has expired') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

const isAxiosGoneError = (error: unknown): error is AxiosError => axios.isAxiosError(error) && error.response?.status === HTTP_STATUS_GONE;

export const acquireSession = async (): Promise<CookielessClientTokens> => {
  const response = await axios.post<CookielessClientTokens>(`${LOOKER_EMBED_BASE_URL}/session/acquire`);
  return response.data;
};

export const generateTokens = async (tokens: GenerateTokensRequest): Promise<CookielessClientTokens> => {
  try {
    const response = await axios.post<CookielessClientTokens>(`${LOOKER_EMBED_BASE_URL}/session/generate`, tokens);
    return response.data;
  } catch (error) {
    if (isAxiosGoneError(error)) {
      throw new SessionExpiredError();
    }
    throw error;
  }
};

export const getContent = async (): Promise<LookerContentResponse> => {
  const response = await axios.get<LookerContentResponse>(`${LOOKER_EMBED_BASE_URL}/content`);
  return response.data;
};
